import { useEffect } from "react";

import {
  getCurrentUser,
  updatePreferredLanguage,
} from "../../services/api/core";
import {
  getLanguageForceHomeHref,
  getLanguageForceLoginHref,
} from "../../utils/auth";

/**
 * `/app/<code>` language override (same behavior as Django `/<code>`).
 *
 * Logged-in users persist `preferred_language` and return to BASE_URL. Guests go
 * to `/app/login/<code>`. Bare home and login URLs keep the saved
 * setting or the login toggle.
 *
 * @param {Object} props Component props.
 * @param {string} props.language Language code to persist.
 * @returns {null} Renders nothing while the redirect runs.
 */
const LanguageForceRedirect = ({ language }) => {
  useEffect(() => {
    let cancelled = false;

    /**
     * Persist the forced language when a session exists, then leave this route.
     *
     * @returns {Promise<void>}
     */
    const applyLanguage = async () => {
      try {
        const response = await getCurrentUser({ force: true });
        if (cancelled) {
          return;
        }
        if (response?.user) {
          try {
            await updatePreferredLanguage(language);
          } catch {
            // Still leave the force route if the write fails.
          }
          if (!cancelled) {
            window.location.replace(getLanguageForceHomeHref());
          }
          return;
        }
      } catch {
        // No session — send the guest to the matching login page.
      }
      if (!cancelled) {
        window.location.replace(getLanguageForceLoginHref(language));
      }
    };

    applyLanguage();
    return () => {
      cancelled = true;
    };
  }, [language]);

  return null;
};

export default LanguageForceRedirect;
